/**
 * The dual string register.
 *
 * Authorised by: product-concept §11 ("a literal-language mode that swaps all playful
 * copy (compost, tossing, temperature metaphors) for precise functional labels...
 * The metaphor layer is skin, never structure — this is a hard requirement"),
 * design-direction §1 ("every string ships in two registers from day one"),
 * implementation-brief §9 ("copy laws and dual string registers from day one") and
 * §12 (`packages/copy` (dual-register strings)).
 *
 * `warm`    — the AUTHORING voice: steady companion, dry warmth, lightly wry. Every string is
 *             written here first and the literal form is written beside it.
 * `literal` — precise functional labels. No metaphor, no humour, no idiom.
 *
 * `warm` used to be described here as "the shipped voice", and it is not: a device ships reading
 * `literal` and offers the metaphors in one control on the settings surface
 * (docs/DEVIATIONS.md D30). Neither register is a reduced version of the other — implementation-brief
 * §11 makes 100% literal coverage a definition-of-done clause, and the linter in `./lint.ts` is
 * what keeps that true of the whole catalogue rather than of the strings someone remembered.
 *
 * A string with no meaningful metaphor still declares both registers. That is not
 * redundancy; it is what makes the register a total function, so literal mode can
 * cover 100% of shipped strings (implementation-brief §11) by construction rather
 * than by audit.
 */

export type Register = 'warm' | 'literal';

/**
 * A law a specific entry is permitted to break, with the reason recorded in the
 * catalogue next to the string.
 *
 * This exists so the linter can enforce laws that have genuine, small exception sets
 * (design-direction §1 permits past tense in an acknowledgement — "fence closed" — but
 * not in instructional copy) without the law being weakened for every string. An
 * exception is a visible, reviewable annotation; a missing law is invisible.
 */
export type LawException =
  | 'tense'
  | 'self-reference'
  /**
   * First-person voice. The only legitimate use is copy written in the USER's voice
   * rather than the app's — "Where was I?" is the user's own question, which is
   * precisely why product-concept §6.3 names the home surface with it.
   */
  | 'first-person'
  | 'sentence-case'
  | 'metaphor';

export type CopyEntry = {
  warm: string;
  literal: string;
  /**
   * Plural forms, selected on the `count` slot. Required by the linter for any entry
   * whose slots include `count` (critique 5.3 — without this the app says "1 items
   * remaining", and this audience notices imprecision immediately).
   *
   * `warm`/`literal` above are the `other` (plural) forms; these are the `one` forms.
   */
  oneWarm?: string;
  oneLiteral?: string;
  /**
   * Set when a string is permitted an exclamation mark: celebration copy at the
   * user's chosen intensity (design-direction §1). Enforced by `lintCatalogue`.
   */
  celebratory?: boolean;
  /** Placeholder names this string interpolates, for the linter to whitelist. */
  slots?: readonly string[];
  /** Laws this entry is deliberately exempt from. Each needs `why`. */
  allow?: readonly LawException[];
  /** Why the exceptions above are justified. Required whenever `allow` is present. */
  why?: string;
};

/** English plural selection. One category boundary, because English has one. */
function isOne(count: unknown): boolean {
  return count === 1 || count === '1';
}

/**
 * Choose the template for a register, taking the count slot into account.
 *
 * Note the fallback: an entry with no `one` form returns the plural form rather than
 * throwing. The linter is what guarantees the `one` form exists for counted strings —
 * a missing plural must fail the build, not fail the user at runtime.
 */
export function pick(
  entry: CopyEntry,
  register: Register,
  values?: Record<string, string | number>,
): string {
  const one = isOne(values?.count);
  if (register === 'literal') {
    return one ? (entry.oneLiteral ?? entry.literal) : entry.literal;
  }
  return one ? (entry.oneWarm ?? entry.warm) : entry.warm;
}

/** Substitute `{name}` slots. Missing values are left visible rather than silently blank. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

/** Every register form an entry declares. Used by the linter to check all of them. */
export function allForms(entry: CopyEntry): Array<{ register: Register; form: string }> {
  const out: Array<{ register: Register; form: string }> = [
    { register: 'warm', form: entry.warm },
    { register: 'literal', form: entry.literal },
  ];
  if (entry.oneWarm !== undefined) out.push({ register: 'warm', form: entry.oneWarm });
  if (entry.oneLiteral !== undefined) out.push({ register: 'literal', form: entry.oneLiteral });
  return out;
}
